import { useCallback, useEffect, useState } from "react";

import { verTablero, type Alerta, type IndicadorProyeccion, type Tablero as DatosDelTablero, type Yo } from "./api";
import { Cargando, Cifra, ErrorDeCarga, Insignia, Pantalla, Progreso, Vacio } from "./componentes";
import { Desglose } from "./Desglose";
import { fraseDeAccion, fraseDeEntidad } from "./eventos-texto";
import { fecha, fechaHora, haceCuanto, numero, plural } from "./formato";
import { mensajeDeError } from "./mensajes";
import { fraseDeMeta, nombreDeMeta } from "./metas-texto";
import { enlaceExterno, esConstruida, type Navegar } from "./navegacion";
import { CONTIENDA } from "./Supuestos";

/**
 * HOY: el centro de mando. De arriba a abajo, en orden de importancia:
 *
 *   1. la cabecera: qué campaña, cuántos días faltan, cuándo se calculó;
 *   2. el marcador: votos para ganar · votos de la estructura · brecha;
 *      promovidos y verificados contra la meta; cobertura de la lista
 *      nominal; secciones con captura;
 *   3. la proyección a la jornada: a qué ritmo hay que ir y a qué ritmo se
 *      va — o «sin base», que no es lo mismo que cero;
 *   4. lo que toca hacer: cada tarea con su botón;
 *   5. el desglose por territorio (migas de pan) y el resto de cifras.
 *
 * Todo se calcula al leer (no hay fotos): el botón «Actualizar» vuelve a
 * preguntar. Lo que la sesión no puede ver no se pinta en cero: `visible`
 * lo esconde.
 */
export function Tablero({ yo, navegar }: { yo: Yo; navegar: Navegar }) {
  const [datos, setDatos] = useState<DatosDelTablero | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setError(null);
    setCargando(true);
    try {
      setDatos(await verTablero());
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (error && !datos) {
    return (
      <Pantalla titulo="Hoy">
        <ErrorDeCarga mensaje={error} reintentar={() => void cargar()} />
      </Pantalla>
    );
  }
  if (!datos) {
    return (
      <Pantalla titulo="Hoy">
        <Cargando que="tu tablero" />
      </Pantalla>
    );
  }

  const { cifras, visible, metas, alertas, actividad, marcador, proyeccion, campana } = datos;
  const ambito = yo.territorio ? yo.territorio.nombre || `${yo.territorio.tipo} ${yo.territorio.clave}` : "sin territorio asignado";
  const v = marcador.votos;

  return (
    <Pantalla
      titulo="Hoy"
      descripcion={
        <>
          {campana ? (
            <>
              <strong>{campana.nombre}</strong> · {CONTIENDA[campana.contienda]}
              {campana.jornada && (
                <>
                  {" "}
                  · jornada {fecha(campana.jornada)} ·{" "}
                  <strong>{datos.dias_a_jornada != null && datos.dias_a_jornada >= 0 ? plural(datos.dias_a_jornada, "día", "días") : "ya pasó"}</strong>
                </>
              )}
              {" · "}
            </>
          ) : null}
          {fecha(datos.hoy)} · {yo.alcance.todo ? "toda la campaña" : ambito}
        </>
      }
      acciones={
        <span className="calculado">
          <span className="apagado chico">calculado a las {fechaHora(datos.calculado).slice(11)}</span>
          <button type="button" className="btn terciario" disabled={cargando} onClick={() => void cargar()}>
            {cargando ? "Calculando…" : "Actualizar"}
          </button>
        </span>
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* ── El marcador ───────────────────────────────────────── */}
      <section aria-labelledby="t-marcador">
        <h2 id="t-marcador">Marcador</h2>
        {marcador.sin_campana ? (
          <Vacio
            titulo="La campaña no está declarada"
            detalle="Sin contienda, jornada y territorio el tablero no puede decir cuántos votos hacen falta ni cuántos días quedan."
            accion={
              yo.capacidades["administrar_metas"] ? (
                <button type="button" className="btn primario" onClick={() => navegar({ pantalla: "supuestos" })}>
                  Declarar la campaña
                </button>
              ) : undefined
            }
          />
        ) : marcador.votos_no_visibles ? (
          <p className="ayuda">Tu sesión no ve las metas de votos; el marcador muestra solo el padrón.</p>
        ) : (
          <div className="cifras marcador">
            <Cifra
              etiqueta="votos para ganar"
              valor={v?.votos_para_ganar != null ? numero(v.votos_para_ganar) : "—"}
              detalle={
                v
                  ? `${numero(v.con_meta)} de ${numero(v.secciones)} secciones con meta${v.sin_base ? ` · ${v.sin_base} sin base` : ""}${v.topadas ? ` · ${v.topadas} topadas` : ""}`
                  : "sin datos"
              }
              alAbrir={() => navegar({ pantalla: "supuestos" })}
            />
            <Cifra
              etiqueta="votos de la estructura"
              valor={v?.meta_estructura != null ? numero(v.meta_estructura) : "—"}
              detalle={
                v?.meta_estructura == null
                  ? "sin meta declarada"
                  : v.meta_estructura_es_de_toda_la_campana
                    ? "de toda la campaña; tu ámbito no se compara"
                    : "lo que la campaña planea conseguir"
              }
              tono={v?.meta_estructura == null ? "aviso" : "neutro"}
              alAbrir={() => navegar({ pantalla: "supuestos" })}
            />
            <Cifra
              etiqueta="brecha"
              valor={v?.brecha == null ? "—" : `${v.brecha > 0 ? "+" : ""}${numero(v.brecha)}`}
              detalle={
                v?.brecha == null
                  ? v?.meta_estructura_es_de_toda_la_campana
                    ? "solo quien ve toda la campaña la compara"
                    : "faltan las dos metas"
                  : v.brecha > 0
                    ? "la fórmula pide más de lo que planea la estructura"
                    : "la estructura planea más de lo que pide la fórmula"
              }
              tono={v?.brecha == null ? "neutro" : v.brecha > 0 ? "peligro" : "exito"}
              alAbrir={() => navegar({ pantalla: "supuestos" })}
            />
            <Cifra
              etiqueta="promovidos"
              valor={v?.votos_para_ganar ? `${numero(marcador.promovidos)} / ${numero(v.votos_para_ganar)}` : numero(marcador.promovidos)}
              detalle={
                v?.votos_para_ganar
                  ? `${Math.round((100 * marcador.promovidos) / v.votos_para_ganar)} % de los votos para ganar · ${numero(marcador.verificados)} verificados`
                  : `${numero(marcador.verificados)} verificados`
              }
              tono={!v?.votos_para_ganar ? "neutro" : marcador.promovidos / v.votos_para_ganar >= 0.8 ? "exito" : marcador.promovidos / v.votos_para_ganar >= 0.5 ? "aviso" : "peligro"}
              alAbrir={() => navegar({ pantalla: "territorio" })}
            />
            <Cifra
              etiqueta="cobertura de la lista nominal"
              valor={marcador.cobertura_pct != null ? `${marcador.cobertura_pct} %` : "—"}
              detalle={marcador.lista_nominal != null ? `${numero(marcador.promovidos)} de ${numero(marcador.lista_nominal)} electores` : "sin lista nominal de la plataforma"}
              alAbrir={() => navegar({ pantalla: "mapa", parametros: { capa: "cobertura" } })}
            />
            <Cifra
              etiqueta="secciones con captura"
              valor={`${numero(marcador.secciones_con_captura)} de ${numero(marcador.secciones)}`}
              detalle={marcador.secciones ? `${Math.round((100 * marcador.secciones_con_captura) / marcador.secciones)} % de tu ámbito` : "sin secciones"}
              alAbrir={() => navegar({ pantalla: "mapa", parametros: { capa: "personas" } })}
            />
            {marcador.secciones_con_responsable != null && (
              <Cifra
                etiqueta="secciones con responsable"
                valor={`${numero(marcador.secciones_con_responsable)} de ${numero(marcador.secciones)}`}
                detalle={
                  marcador.secciones
                    ? `${numero(marcador.secciones - marcador.secciones_con_responsable)} vacantes · ${Math.round((100 * marcador.secciones_con_responsable) / marcador.secciones)} %`
                    : "sin secciones"
                }
                tono={
                  !marcador.secciones
                    ? "neutro"
                    : marcador.secciones_con_responsable === marcador.secciones
                      ? "exito"
                      : marcador.secciones_con_responsable * 2 >= marcador.secciones
                        ? "aviso"
                        : "peligro"
                }
                alAbrir={() => navegar({ pantalla: "estructura", parametros: { ver: "vacantes" } })}
              />
            )}
          </div>
        )}
      </section>

      {/* ── La proyección ─────────────────────────────────────── */}
      {!marcador.sin_campana && (
        <section aria-labelledby="t-proyeccion">
          <h2 id="t-proyeccion">Proyección a la jornada</h2>
          <p className="ayuda">
            Ritmo observado = promedio diario de las últimas cuatro semanas completas con captura. Con menos, «sin base»: no se inventa.
          </p>
          <div className="contenedor-tabla">
            <table className="tabla">
              <caption>Cuánto falta, a qué ritmo hay que ir y a qué ritmo se va, por indicador.</caption>
              <thead>
                <tr>
                  <th scope="col">Indicador</th>
                  <th scope="col" className="numero">Avance</th>
                  <th scope="col" className="numero">Meta</th>
                  <th scope="col" className="numero">Faltan</th>
                  <th scope="col" className="numero">Ritmo requerido / día</th>
                  <th scope="col" className="numero">Ritmo observado / día</th>
                  <th scope="col" className="numero">Llegaría a</th>
                  <th scope="col">Últimas semanas</th>
                </tr>
              </thead>
              <tbody>
                {proyeccion.map((p) => (
                  <FilaProyeccion key={p.indicador} p={p} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── La estructura contra el plan ──────────────────────── */}
      {visible.estructura && cifras.estructura && (
        <section aria-labelledby="t-estructura">
          <div className="encabezado">
            <h2 id="t-estructura">Estructura</h2>
            <button type="button" className="btn terciario" onClick={() => navegar({ pantalla: "estructura" })}>
              Abrir la estructura
            </button>
          </div>
          {!cifras.estructura.con_plan && <p className="ayuda">Sin plan por rol: se enseña lo que hay, no lo que falta.</p>}
          {cifras.estructura.con_plan && cifras.estructura.plan_es_de_toda_la_campana && (
            <p className="ayuda">Lo vigente es lo de tu ámbito; el plan es de toda la campaña y «faltan» se lee en el tablero de quien la ve entera.</p>
          )}
          <div className="cifras">
            {cifras.estructura.roles.map((r) => (
              <Cifra
                key={r.clave}
                etiqueta={r.plural.toLowerCase()}
                valor={r.plan != null && r.faltan != null ? `${numero(r.vigentes)} / ${numero(r.plan)}` : numero(r.vigentes)}
                detalle={r.plan == null ? "sin plan" : r.faltan == null ? `plan de la campaña: ${numero(r.plan)}` : r.faltan ? `faltan ${numero(r.faltan)}` : "plan cubierto"}
                tono={r.plan == null || r.faltan == null ? "neutro" : r.faltan ? (r.faltan * 2 > r.plan ? "peligro" : "aviso") : "exito"}
                alAbrir={() => navegar({ pantalla: "estructura", parametros: { rol: r.clave } })}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Lo que toca hacer ─────────────────────────────────── */}
      <section aria-labelledby="t-tareas">
        <h2 id="t-tareas">Lo que toca hacer</h2>
        {alertas.length === 0 ? (
          <Vacio titulo="Nada pendiente que el sistema vea" detalle="Sin capturas esperando, metas al día y cuentas en orden. Lo que no está aquí es porque todavía no se mide." />
        ) : (
          <ol className="tareas">
            {alertas.map((a) => (
              <Tarea key={a.clave} alerta={a} navegar={navegar} />
            ))}
          </ol>
        )}
      </section>

      {/* ── El desglose ───────────────────────────────────────── */}
      <section aria-labelledby="t-desglose">
        <div className="encabezado">
          <h2 id="t-desglose">Por territorio</h2>
          <button type="button" className="btn terciario" onClick={() => navegar({ pantalla: "territorio" })}>
            Abrir el territorio
          </button>
        </div>
        <Desglose yo={yo} nodoInicial={null} navegar={navegar} compacto />
      </section>

      {/* ── Tu padrón ─────────────────────────────────────────── */}
      <section aria-labelledby="t-padron">
        <h2 id="t-padron">Tu padrón</h2>
        <div className="cifras">
          {!yo.alcance.todo && cifras.territorio.personas_en_ambito !== cifras.padron.personas ? (
            <Cifra etiqueta="personas en tu ámbito" valor={numero(cifras.territorio.personas_en_ambito)} detalle={`${numero(cifras.padron.personas)} en todo lo que alcanzas a ver`} alAbrir={() => navegar({ pantalla: "territorio" })} />
          ) : (
            <Cifra etiqueta="personas" valor={numero(cifras.padron.personas)} detalle={`en ${plural(cifras.padron.hogares, "hogar", "hogares")}`} alAbrir={() => navegar({ pantalla: "territorio" })} />
          )}
          <Cifra
            etiqueta="esta semana"
            valor={numero(cifras.padron.ultimos_7_dias)}
            detalle={`${numero(cifras.padron.ultimos_14_dias)} en 14 días`}
            tono={cifras.padron.ultimos_7_dias === 0 ? "aviso" : "neutro"}
            alAbrir={() => navegar({ pantalla: "mapa", parametros: { capa: "ritmo" } })}
          />
          <Cifra etiqueta="con teléfono" valor={numero(cifras.padron.con_telefono)} detalle={`${Math.round((100 * cifras.padron.con_telefono) / Math.max(cifras.padron.personas, 1))} % del padrón`} sinAccion="La lista con teléfono sale por Sacar datos, que exige exportar" />
          {cifras.padron.sin_seccion > 0 &&
            (visible.bandeja ? (
              <Cifra etiqueta="sin sección resuelta" valor={numero(cifras.padron.sin_seccion)} detalle="no cuentan en ninguna meta" tono="aviso" enlace={enlaceExterno("conciliacion")!} />
            ) : (
              <Cifra etiqueta="sin sección resuelta" valor={numero(cifras.padron.sin_seccion)} detalle="no cuentan en ninguna meta" tono="aviso" sinAccion="Se corrigen en la bandeja, que exige conciliar" />
            ))}
        </div>
      </section>

      {visible.bandeja && cifras.bandeja && (
        <section aria-labelledby="t-bandeja">
          <h2 id="t-bandeja">Bandeja</h2>
          <div className="cifras">
            <Cifra
              etiqueta="esperan revisión"
              valor={numero(cifras.bandeja.pendientes)}
              detalle={cifras.bandeja.pendientes > 0 ? `la más antigua lleva ${plural(cifras.bandeja.mas_vieja_dias, "día", "días")}` : "nada esperando"}
              tono={cifras.bandeja.mas_vieja_dias >= 3 ? "peligro" : cifras.bandeja.pendientes > 0 ? "aviso" : "exito"}
              enlace={enlaceExterno("conciliacion")!}
            />
            <Cifra etiqueta="en revisión ahora" valor={numero(cifras.bandeja.en_revision)} detalle="alguien ya las tomó" enlace={enlaceExterno("conciliacion")!} />
            <Cifra etiqueta="aprobadas esta semana" valor={numero(cifras.bandeja.aprobadas_7_dias)} detalle={`${numero(cifras.bandeja.rechazadas)} rechazadas en total`} enlace={enlaceExterno("conciliacion")!} />
          </div>
        </section>
      )}

      {visible.cuentas && cifras.cuentas && (
        <section aria-labelledby="t-cuentas">
          <h2 id="t-cuentas">Cuentas</h2>
          <div className="cifras">
            <Cifra etiqueta="activas" valor={numero(cifras.cuentas.activas)} detalle={`${numero(cifras.cuentas.capturistas)} capturan en campo`} alAbrir={() => navegar({ pantalla: "usuarios" })} />
            <Cifra
              etiqueta="sin contraseña"
              valor={numero(cifras.cuentas.sin_contrasena)}
              detalle={cifras.cuentas.sin_contrasena > 0 ? "no pueden entrar" : "todas pueden entrar"}
              tono={cifras.cuentas.sin_contrasena > 0 ? "aviso" : "exito"}
              alAbrir={() => navegar({ pantalla: "usuarios", parametros: { filtro: "sin-contrasena" } })}
            />
            <Cifra etiqueta="inactivas" valor={numero(cifras.cuentas.inactivas)} alAbrir={() => navegar({ pantalla: "usuarios", parametros: { filtro: "inactivas" } })} />
          </div>
        </section>
      )}

      {visible.operacion && cifras.operacion && (
        <section aria-labelledby="t-operacion">
          <h2 id="t-operacion">Llamadas</h2>
          <div className="cifras">
            <Cifra etiqueta="en cola" valor={numero(cifras.operacion.abiertas)} detalle="asignaciones abiertas" enlace={enlaceExterno("operacion")!} />
            <Cifra
              etiqueta="llamadas hoy"
              valor={numero(cifras.operacion.intentos_hoy)}
              detalle={`${numero(cifras.operacion.intentos_7_dias)} en 7 días`}
              tono={cifras.operacion.abiertas > 0 && cifras.operacion.intentos_hoy === 0 ? "aviso" : "neutro"}
              enlace={enlaceExterno("operacion")!}
            />
          </div>
        </section>
      )}

      {visible.captacion && cifras.captacion && (
        <section aria-labelledby="t-captacion">
          <h2 id="t-captacion">Captación</h2>
          <div className="cifras">
            <Cifra etiqueta="registros esta semana" valor={numero(cifras.captacion.registros_7_dias)} detalle={`${numero(cifras.padron.por_captacion)} personas llegaron por enlace en total`} sinAccion="Los registros de captación entran al padrón y a la bandeja; se revisan ahí" />
            <Cifra etiqueta="enlaces vivos" valor={numero(cifras.captacion.enlaces_vivos)} detalle={plural(cifras.captacion.referentes, "referente activo", "referentes activos")} sinAccion="Los enlaces se administran por la API de captación; esta app no los dibuja todavía" />
          </div>
        </section>
      )}

      {visible.metas && metas.length > 0 && (
        <section aria-labelledby="t-metas">
          <div className="encabezado">
            <h2 id="t-metas">Metas de captura</h2>
            <button type="button" className="btn terciario" onClick={() => navegar({ pantalla: "metas" })}>
              Ver todas las metas
            </button>
          </div>
          <ul className="metas-lista">
            {metas.slice(0, 4).map((m) => {
              const f = fraseDeMeta(m);
              return (
                <li key={m.id} className="meta-resumen">
                  <div className="meta-cabeza">
                    <button type="button" className="btn terciario" onClick={() => navegar({ pantalla: "territorio", parametros: { nodo: m.territorio_id } })}>
                      {nombreDeMeta(m)}
                    </button>
                    <Insignia tono={f.tono}>{f.titulo}</Insignia>
                  </div>
                  <Progreso valor={m.logrado} max={m.objetivo} tono={f.tono} texto={`${numero(m.logrado)} de ${numero(m.objetivo)} · ${m.proyeccion.porcentaje} %`} />
                  <p className="chico apagado">{f.detalle}</p>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {visible.eventos && (
        <section aria-labelledby="t-actividad">
          <div className="encabezado">
            <h2 id="t-actividad">Actividad reciente</h2>
            <button type="button" className="btn terciario" onClick={() => navegar({ pantalla: "eventos" })}>
              Ver la bitácora
            </button>
          </div>
          {actividad.length === 0 ? (
            <p className="apagado">Todavía no pasa nada.</p>
          ) : (
            <ul className="actividad">
              {actividad.slice(0, 8).map((e) => (
                <li key={e.id}>
                  <span className="apagado chico">{haceCuanto(e.ocurrido)}</span> <strong>{e.quien ?? "alguien sin sesión"}</strong> {fraseDeAccion(e.accion)}
                  {e.entidad && <span className="apagado"> · {fraseDeEntidad(e.entidad, e.entidad_id)}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </Pantalla>
  );
}

const NOMBRE_INDICADOR: Record<string, string> = {
  promovidos: "Promovidos",
  verificados: "Verificados",
  responsables: "Secciones con responsable",
};

function FilaProyeccion({ p }: { p: IndicadorProyeccion }) {
  const max = Math.max(1, ...p.serie.map((s) => s.n));
  return (
    <tr>
      <td>{NOMBRE_INDICADOR[p.indicador] ?? p.indicador}</td>
      <td className="numero">{numero(p.avance)}</td>
      <td className="numero apagado">{p.meta != null ? numero(p.meta) : "—"}</td>
      <td className="numero">{p.faltan != null ? numero(p.faltan) : "—"}</td>
      <td className="numero">{p.ritmo_requerido_dia != null ? p.ritmo_requerido_dia.toLocaleString("es-MX") : "—"}</td>
      <td className="numero" title={p.sin_base ?? undefined}>
        {p.ritmo_observado_dia != null ? p.ritmo_observado_dia.toLocaleString("es-MX") : <Insignia tono="neutro">sin base</Insignia>}
      </td>
      <td className="numero">
        {p.proyeccion_a_jornada == null ? "—" : <Insignia tono={p.alcanza ? "exito" : "peligro"}>{numero(p.proyeccion_a_jornada)}</Insignia>}
      </td>
      <td>
        <span className="serie" role="img" aria-label={`Últimas ${p.serie.length} semanas: ${p.serie.map((s) => s.n).join(", ")}`}>
          {p.serie.map((s) => (
            <span key={s.semana} className="serie-barra" style={{ height: `${Math.max(4, Math.round((100 * s.n) / max))}%` }} title={`${s.semana}: ${s.n}`} />
          ))}
        </span>
      </td>
    </tr>
  );
}

const TONO_DE_NIVEL = { alta: "peligro", media: "aviso", baja: "neutro" } as const;
const TEXTO_DE_NIVEL = { alta: "atender", media: "revisar", baja: "en espera" } as const;

function Tarea({ alerta, navegar }: { alerta: Alerta; navegar: Navegar }) {
  const { accion } = alerta;
  const externo = enlaceExterno(accion.pantalla);
  return (
    <li className={`tarea nivel-${alerta.nivel}`}>
      <div className="tarea-texto">
        <Insignia tono={TONO_DE_NIVEL[alerta.nivel]}>{TEXTO_DE_NIVEL[alerta.nivel]}</Insignia>
        <strong>{alerta.titulo}</strong>
        <p className="chico apagado">{alerta.detalle}</p>
      </div>
      {esConstruida(accion.pantalla) ? (
        <button type="button" className="btn secundario" onClick={() => navegar({ pantalla: accion.pantalla, parametros: accion.parametros })}>
          {accion.texto}
        </button>
      ) : externo ? (
        <a className="btn secundario" href={externo}>
          {accion.texto}
        </a>
      ) : null}
    </li>
  );
}
